import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import { intersects } from '@/app/gridGL/helpers/intersects';
import { HtmlCell } from '@/app/gridGL/HTMLGrid/htmlCells/HtmlCell';
import { getFrozenPaneForCell, type FrozenPane } from '@/app/gridGL/pixiApp/frozenLayout';
import type { JsCoordinate, JsHtmlOutput, JsRenderCodeCell, SheetInfo } from '@/app/quadratic-core-types';
import type { Point } from 'pixi.js';
import { Rectangle } from 'pixi.js';
import './HtmlCells.css';

class HTMLCellsHandler {
  // The four pane containers a chart can live in. `body` is the scrollable
  // grid; the others are the pinned frozen panes. Each is a world-transformed
  // content div owned by HTMLGridContainer (see frozenLayout for the geometry).
  private containers: {
    body?: HTMLDivElement;
    corner?: HTMLDivElement;
    top?: HTMLDivElement;
    left?: HTMLDivElement;
  } = {};

  private cells: Set<HtmlCell> = new Set();

  // Cache of the last HTML output data for restoration after remounting
  private cachedHtmlOutput: JsHtmlOutput[] = [];

  // Track whether the handler is attached to the DOM
  private isAttached = false;

  // dedupes pane re-assignment to one per frame
  private reassignScheduled = false;

  constructor() {
    events.on('htmlOutput', this.htmlOutput);
    events.on('htmlUpdate', this.htmlUpdate);
    events.on('changeSheet', this.changeSheet);
    events.on('sheetInfoUpdate', this.handleSheetInfoUpdate);
    events.on('sheetOffsetsUpdated', (sheetId) => this.updateOffsets([sheetId]));
  }

  // Registers the scrollable body container. Called by HTMLGridContainer when
  // its body pane mounts.
  attach = (parent: HTMLDivElement) => {
    this.containers.body = parent;
    this.isAttached = true;

    // Restore cached HTML cells if we have any (e.g., returning from dashboard)
    if (this.cachedHtmlOutput.length > 0 && this.cells.size === 0) {
      this.prepareCells([], this.cachedHtmlOutput);
    } else {
      this.scheduleReassign();
    }
  };

  detach = () => {
    this.isAttached = false;
    this.cells.clear();
    this.containers = {};
  };

  // Registers the pinned frozen-pane containers. Called by HTMLGridContainer.
  setFrozenContainers = (containers: { corner: HTMLDivElement; top: HTMLDivElement; left: HTMLDivElement }) => {
    this.containers.corner = containers.corner;
    this.containers.top = containers.top;
    this.containers.left = containers.left;
    this.scheduleReassign();
  };

  clearFrozenContainers = () => {
    this.containers.corner = undefined;
    this.containers.top = undefined;
    this.containers.left = undefined;
  };

  // Clear the cache and destroy all cells when switching to a different file or version
  clearCache = () => {
    this.cachedHtmlOutput = [];
    this.cells.forEach((cell) => cell.destroy());
    this.cells.clear();
  };

  private htmlOutput = (htmlCells: JsHtmlOutput[]) => {
    // Cache the HTML output for restoration after remounting
    this.cachedHtmlOutput = htmlCells;
    // Skip DOM operations if not attached
    if (!this.isAttached) return;
    this.prepareCells([...this.cells], htmlCells);
  };

  private htmlUpdate = (data: JsHtmlOutput) => {
    // Update cache
    const cacheIndex = this.cachedHtmlOutput.findIndex(
      (cached) => cached.sheet_id === data.sheet_id && cached.x === data.x && cached.y === data.y
    );
    if (data.html) {
      if (cacheIndex === -1) {
        this.cachedHtmlOutput.push(data);
      } else {
        this.cachedHtmlOutput[cacheIndex] = data;
      }
    } else if (cacheIndex !== -1) {
      this.cachedHtmlOutput.splice(cacheIndex, 1);
    }

    // Skip DOM operations if not attached
    if (!this.isAttached) return;

    // update an existing cell
    for (const cell of this.cells) {
      if (cell.isOutputEqual(data)) {
        if (data.html) {
          cell.update(data);
          this.placeCell(cell);
        } else {
          cell.destroy();
          this.cells.delete(cell);
        }
        return;
      }
    }

    // or add a new cell
    if (data.html) {
      this.createCell(data);
    }
  };

  /**
   * Creates and places a cell for `htmlCell`, or does nothing if its sheet is
   * gone.
   *
   * A chart whose sheet no longer exists can't be placed — `HtmlCell` needs the
   * sheet to compute its screen rectangle and throws without it. That's a
   * recoverable condition rather than a bug, and core never tells us to drop
   * those charts: `send_html_cells` skips any sheet it can't find, so deleting a
   * sheet emits no `htmlUpdate` with `html: null` for its charts, and the full
   * `htmlOutput` list is only sent once, when the file loads. So
   * `cachedHtmlOutput` keeps the deleted sheet's charts and `attach` tried to
   * rebuild them on the next mount, throwing inside HTMLGridContainer's mount
   * effect and taking the grid down with it (INTERNAL-6GC).
   *
   * Cache entries are left alone: the sheet can come back (undo re-adds it and
   * re-runs its code cells, which re-sends the chart), and `sheets` is empty
   * mid-file-switch, so dropping them here would lose charts that are still
   * live.
   */
  private createCell(htmlCell: JsHtmlOutput): void {
    if (!sheets.getById(htmlCell.sheet_id)) return;
    const cell = new HtmlCell(htmlCell);
    this.cells.add(cell);
    this.placeCell(cell);
  }

  private changeSheet = () => {
    this.cells.forEach((cell) => cell.changeSheet(sheets.current));
    this.scheduleReassign();
  };

  private handleSheetInfoUpdate = (sheetInfo: SheetInfo) => {
    // Frozen rows/columns may have changed, which moves charts between panes.
    // Only the current sheet's charts are visible, so re-place on any update to
    // it. Deferred (see scheduleReassign) so Sheets has applied the new info.
    if (sheetInfo.sheet_id === sheets.current) {
      this.scheduleReassign();
    }
  };

  // The DOM container a chart should live in based on its anchor cell.
  private containerForPane(pane: FrozenPane): HTMLDivElement | undefined {
    switch (pane) {
      case 'corner':
        return this.containers.corner ?? this.containers.body;
      case 'top':
        return this.containers.top ?? this.containers.body;
      case 'left':
        return this.containers.left ?? this.containers.body;
      case 'body':
        return this.containers.body;
    }
  }

  // Appends a chart's div into the pane that owns its anchor cell (independent
  // of scroll, so the iframe is never reparented — which would reload it). The
  // pane's clip expands on untorn axes so the iframe shows the contiguous live
  // region; torn-away slices are drawn by the pixi chart placeholders instead.
  // Charts on a non-current sheet stay in the body container (hidden anyway).
  private placeCell(cell: HtmlCell) {
    const isCurrent = cell.sheet.id === sheets.current;
    const anchorPane: FrozenPane = isCurrent ? getFrozenPaneForCell(cell.x, cell.y) : 'body';
    const target = this.containerForPane(anchorPane);
    if (target && cell.div.parentElement !== target) {
      target.appendChild(cell.div);
    }
  }

  private scheduleReassign() {
    if (this.reassignScheduled) return;
    this.reassignScheduled = true;
    requestAnimationFrame(() => {
      this.reassignScheduled = false;
      this.reassignPanes();
    });
  }

  // Re-places every chart into its correct pane (after a freeze or sheet change).
  private reassignPanes() {
    if (!this.isAttached) return;
    this.cells.forEach((cell) => this.placeCell(cell));
  }

  private prepareCells(old: HtmlCell[], cells: JsHtmlOutput[]) {
    // update or add new cells
    cells.forEach((htmlCell) => {
      const index = old.findIndex((cell) => cell.isOutputEqual(htmlCell));
      if (index === -1) {
        this.createCell(htmlCell);
      } else {
        old[index].update(htmlCell);
        this.placeCell(old[index]);
        old.splice(index, 1);
      }
    });

    // remove old cells
    old.forEach((cell) => {
      cell.destroy();
      this.cells.delete(cell);
    });
  }

  clearHighlightEdges() {
    this.cells.forEach((cell) => cell.clearHighlightEdges());
  }

  // cells are store in back to front order
  // return cells in front to back order
  getCells(): HtmlCell[] {
    return Array.from(this.cells.values()).reverse();
  }

  // handle changes to offsets
  updateOffsets(sheetIds: string[]) {
    this.cells.forEach((cell) => {
      if (sheetIds.includes(cell.sheet.id)) {
        cell.updateOffsets();
        // a resize/offset change can move the chart across a freeze boundary
        this.placeCell(cell);
      }
    });
  }

  // moves the chart to top
  // updates the z-index and the order of the cells
  movetoTop(cell: HtmlCell) {
    const cells = this.getCells();
    if (cells.length < 2) return;
    const topCell = cells[0];
    if (topCell === cell) return;
    const topCellZIndex = parseInt(topCell.div.style.zIndex || '0');
    cell.div.style.zIndex = (topCellZIndex + 1).toString();
    // remove and add cell to the set to update the order
    this.cells.delete(cell);
    this.cells.add(cell);
  }

  checkHover(world: Point): JsCoordinate | undefined {
    const cells = this.getCells();
    for (const cell of cells) {
      if (cell.sheet.id !== sheets.current) continue;
      const bounds = new Rectangle(
        cell.div.offsetLeft,
        cell.div.offsetTop,
        cell.div.offsetWidth,
        cell.div.offsetHeight
      );
      if (intersects.rectanglePoint(bounds, world)) {
        return { x: cell.x, y: cell.adjustedY };
      }
    }
  }

  // returns true if the cell is an html cell
  isHtmlCell(x: number, y: number): boolean {
    return this.getCells().some((cell) => cell.x === x && cell.adjustedY === y && cell.sheet.id === sheets.current);
  }

  // returns true if the Pos overlaps with the output of an html cell
  contains(x: number, y: number): boolean {
    return this.getCells().some((cell) => cell.contains(x, y));
  }

  findCodeCell = (sheet_id: string, x: number, y: number): HtmlCell | undefined => {
    return this.getCells().find((cell) => cell.sheet.id === sheet_id && cell.contains(x, y));
  };

  codeCellPixelWidth = (sheet_id: string, x: number, y: number): number | undefined => {
    const cell = this.findCodeCell(sheet_id, x, y);
    if (cell) {
      return cell.width;
    }
  };

  // Returns a plotly.js-rendered image (at the chart's displayed size) for the
  // chart anchored at (x, y), so the pixi placeholders match the live iframe.
  // Falls back to the stored server image when no iframe is available.
  getPlaceholderImage = (sheetId: string, x: number, y: number): Promise<string | undefined> => {
    const cell = this.getCells().find((c) => c.sheet.id === sheetId && c.x === x && c.y === y);
    return cell ? cell.getPlotlyImageUrl() : Promise.resolve(undefined);
  };

  showActive(codeCell: JsRenderCodeCell) {
    const cell = this.getCells().find(
      (cell) => cell.x === codeCell.x && cell.y === codeCell.y && cell.sheet.id === sheets.current
    );
    cell?.activate();
  }

  hideActive(codeCell: JsRenderCodeCell) {
    const cell = this.getCells().find(
      (cell) => cell.x === codeCell.x && cell.y === codeCell.y && cell.sheet.id === sheets.current
    );
    cell?.deactivate();
  }

  disable() {
    this.getCells().forEach((cell) => cell.deactivate());
  }

  temporarilyDisable() {
    this.getCells().forEach((cell) => cell.temporarilyDeactivate());
  }

  enable() {
    this.getCells().forEach((cell) => cell.reactivate());
  }
}

export const htmlCellsHandler = new HTMLCellsHandler();
